import type { ReactNode } from "react";
import { styled } from "~/common/mui";

/**
 * ProductLineGrid - Compound component for cart/checkout line item layout.
 *
 * Uses CSS Grid with explicit `order` for mobile (DOM order independent).
 * Mobile: 12-column grid, elements ordered via CSS `order` property.
 * Desktop: 6-column grid (180px + 5fr), elements placed with gridColumn + gridRow.
 */

// Root grid container
const GridRoot = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
  columnGap: "16px",
  rowGap: "16px",

  [theme.breakpoints.up("desktop-m")]: {
    gridTemplateColumns: "180px repeat(5, minmax(0px, 1fr))",
  },
}));

// Row 1 slots
const StockIndicator = styled("div")(({ theme }) => ({
  order: 1,
  gridColumn: "1 / -1",
  justifySelf: "flex-start",

  [theme.breakpoints.up("desktop-m")]: {
    alignSelf: "flex-start",
    gridColumn: "1 / span 1",
    gridRow: 1,
  },
}));

const Quantity = styled("div")(({ theme }) => ({
  order: 2,
  gridColumn: "span 4",
  maxWidth: "200px",

  [theme.breakpoints.up("desktop-m")]: {
    gridColumn: "2 / span 1",
    gridRow: 1,
  },
}));

const Packaging = styled("div")(({ theme }) => ({
  order: 3,
  gridColumn: "span 4",

  [theme.breakpoints.up("desktop-m")]: {
    gridColumn: "3 / span 1",
    gridRow: 1,
  },
}));

const DeliveryDate = styled("div")(({ theme }) => ({
  order: 4,
  gridColumn: "span 4",

  [theme.breakpoints.up("desktop-m")]: {
    gridColumn: "4 / span 1",
    gridRow: 1,
  },
}));

const NetPrice = styled("div")(({ theme }) => ({
  order: 5,
  gridColumn: "span 4",

  [theme.breakpoints.up("desktop-m")]: {
    gridColumn: "5 / span 1",
    gridRow: 1,
  },
}));

const TotalPrice = styled("div")(({ theme }) => ({
  order: 6,
  gridColumn: "span 4",
  justifySelf: "flex-start",

  [theme.breakpoints.up("desktop-m")]: {
    gridColumn: "6 / span 1",
    gridRow: 1,
  },
}));

// Row 2 slots
const PONumber = styled("div")(({ theme }) => ({
  order: 7,
  gridColumn: "1 / span 4",

  [theme.breakpoints.up("desktop-m")]: {
    gridColumn: "2 / span 1",
    gridRow: 2,
  },
}));

const Aliases = styled("div")(({ theme }) => ({
  order: 8,
  gridColumn: "1 / span 4",

  [theme.breakpoints.up("desktop-m")]: {
    gridColumn: "3 / span 1",
    gridRow: 2,
  },
}));

const ContractDetails = styled("div")(({ theme }) => ({
  order: 9,
  gridColumn: "9 / span 4",

  [theme.breakpoints.up("desktop-m")]: {
    gridColumn: "4 / span 1",
    gridRow: 2,
  },
}));

const QuoteDetails = styled("div")(({ theme }) => ({
  order: 10,
  gridColumn: "9 / span 4",

  [theme.breakpoints.up("desktop-m")]: {
    gridColumn: "4 / span 1",
    gridRow: 2,
  },
}));

const PriceReference = styled("div")(({ theme }) => ({
  order: 11,
  gridColumn: "span 4",

  [theme.breakpoints.up("desktop-m")]: {
    gridColumn: "5 / span 1",
    gridRow: 2,
  },
}));

const GPSD = styled("div")(({ theme }) => ({
  order: 12,
  gridColumn: "5 / span 4",

  [theme.breakpoints.up("desktop-m")]: {
    gridColumn: "6 / span 1",
    gridRow: 2,
  },
}));

// Row 3 slots
const Messages = styled("div")(({ theme }) => ({
  order: 13,
  gridColumn: "1 / -1",
  gridRow: 3,

  [theme.breakpoints.up("desktop-m")]: {
    gridColumn: "1 / span 4",
    gridRow: 3,
  },
}));

// Root component
interface ProductLineGridProps {
  children: ReactNode;
}

const ProductLineGridRoot = ({ children }: ProductLineGridProps) => <GridRoot>{children}</GridRoot>;

export const ProductLineGrid = Object.assign(ProductLineGridRoot, {
  StockIndicator,
  Quantity,
  Packaging,
  DeliveryDate,
  NetPrice,
  TotalPrice,
  PONumber,
  Aliases,
  ContractDetails,
  QuoteDetails,
  PriceReference,
  GPSD,
  Messages,
});
